import { ButtonCus } from '@/components/common/Button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/common/Form';
import { Textarea } from '@/components/common/Textarea';
import { useStore } from '@/hooks/useStore';
import yup from '@/services/yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { flowResult } from 'mobx';
import { observer } from 'mobx-react-lite';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface CancelOrderProps {
    orderIds: number[],
    reload?: Function,
    isForEnd?: boolean
}
export default observer(function CancelOrder({ orderIds, reload, isForEnd = false }: CancelOrderProps) {
    // hook
    const { t } = useTranslation();
    // store
    const { reservationStore: { cancelReservation, cancelReservationEND }, modalStore: { hideAllModals, showAlertModal } } = useStore();
    // validate schema
    const validateSchema = yup.object().shape({
        content: yup
            .string()
            .trim().optional()
    });

    type FormData = yup.InferType<typeof validateSchema>;

    // state
    const form = useForm<FormData>({
        resolver: yupResolver(validateSchema),
        mode: 'onChange',
        defaultValues: {
            content: ''
        }
    });

    // function
    const onSubmit = async (data: FormData) => {
        showAlertModal({
            id: 'confirm-cancel',
            size: 'sm',
            content: (
                <p className='text-center whitespace-pre-line'>
                    {t('messages.confirm_cancel_booking')}
                </p>
            ),
            isConfirmModal: true,
            saveButton: t('button.yes'),
            cancelButton: t('button.no'),
            onSave: async () => {
                await onSubmitData(data);
            }
        });
    };

    const onSubmitData = async (data: any) => {
        const res = await flowResult(isForEnd === false ? cancelReservation({ orderIds, content: data.content || '' }) : cancelReservationEND({ id: orderIds[0], note: data.content || '' }));
        if (res) {
            hideAllModals();
            showAlertModal({
                id: 'cancel-success',
                type: 'success',
                content: t('messages.cancel_success'),
                onClickXIcon: () => {
                    reload && reload();
                }
            });
        } else {
            showAlertModal({
                id: 'cancel-failure',
                type: 'error',
                content: t('messages.cancel_failure'),
                onClickXIcon: () => {
                    reload && reload();
                }
            });
        }
    };

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)}>
                <div>
                    <FormField
                        control={form.control}
                        name='content'
                        render={({ field }) => (
                            <FormItem>
                                <FormLabel>{t('words_title.note')}</FormLabel>
                                <FormControl>
                                    <Textarea {...field} rows={5} maxLength={500} placeholder={t('placeholder.enter')}></Textarea>
                                </FormControl>
                                <FormMessage />
                            </FormItem>
                        )}
                    />
                </div>
                <div>
                    <div className='mt-8 flex justify-center'>
                        <ButtonCus variant='default' size='sm' disabled={form.formState.isSubmitting} type='submit'>{t('button.cancel_booking')}</ButtonCus>
                    </div>
                </div>
            </form>
        </Form>
    );
});
